import { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import GoBack from "../components/GoBack";
import useFlow from "../hooks/useFlow";
import useStore from "../hooks/useStore";

const TOGGLES = [
  { key: "offeringEnabled", label: "Show offers after check-in" },
  { key: "showLeadsDots", label: "Show lead dots on the check-in list" },
  { key: "showPhoneNumpad", label: "Use the numpad for phone entry" },
  { key: "showFullKeyboard", label: "Show the full on-screen keyboard" },
  { key: "stripeCheckoutEnabled", label: "Take payments through Stripe" },
];

const PIN_LENGTH_MIN = 4;
const FEATURED_SLOTS = 8;
const PLAN_OPTION_SLOTS = 3;
const AUTOSAVE_DELAY_MS = 1200;

export default function Settings() {
  const {
    selectedSiteId,
    selectedLocationId,
    locationPreferences,
    loadLocationPreferences,
    saveCheckinSettings,
    loadCheckinRooms,
    uploadRoomMap,
    uploadLogo,
    resetStoreClient,
  } = useFlow();

  const { searchItemById } = useStore();

  // The PIN is held only while this page is open — leaving or reloading drops it.
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  // Skips the autosave that would otherwise fire the moment the form is seeded.
  const seeded = useRef(false);
  const [error, setError] = useState(null);

  // Featured products, in the order they appear on the Purchase screen.
  const [featured, setFeatured] = useState([]);
  const [plan, setPlan] = useState({ special: null, others: [], small: null });
  // { list: "featured" | "others", index } or { slot: "special" | "small" }
  const [picking, setPicking] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [roomMaps, setRoomMaps] = useState([]);
  const [rooms, setRooms] = useState(null);
  const [addingRoom, setAddingRoom] = useState(false);
  const [uploadingRoom, setUploadingRoom] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [storeClientId, setStoreClientId] = useState(null);
  const [resettingClient, setResettingClient] = useState(false);
  const [itemQuery, setItemQuery] = useState("");
  const [itemOptions, setItemOptions] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Current values come from the preferences the kiosk already loaded, so
  // unlocking needs no extra round trip.
  useEffect(() => {
    if (!locationPreferences) return;
    setForm({
      title: locationPreferences.title || "",
      subTitle: locationPreferences.subTitle || "",
      primaryColor: locationPreferences.primaryColor || "#1BC8CA",
      secondaryColor: locationPreferences.secondaryColor || "#6c757d",
      timezone: locationPreferences.timezone || moment.tz.guess(),
      logo: locationPreferences.logo || "",
      ...Object.fromEntries(
        TOGGLES.map(({ key }) => [key, locationPreferences[key] === true]),
      ),
    });

    setStoreClientId(locationPreferences.defaultClientId || null);
    setFeatured((locationPreferences.store?.topItems || []).filter(Boolean));
    setRoomMaps((locationPreferences.roomMaps || []).filter((r) => r?.src));
    setPlan({
      special: locationPreferences.planOffer?.special || null,
      others: (locationPreferences.planOffer?.others || []).filter(Boolean),
      small: locationPreferences.planOffer?.small || null,
    });
  }, [locationPreferences]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleUnlock = (e) => {
    e.preventDefault();
    setError(null);

    if (pin.length < PIN_LENGTH_MIN) {
      setError(`Enter your ${PIN_LENGTH_MIN}-digit PIN.`);
      return;
    }
    seeded.current = false;
    setUnlocked(true);
    // Doubles as an early PIN check: stored room maps carry no names, so this
    // is also what labels them.
    loadRooms();
  };

  const loadRooms = async () => {
    try {
      setRooms(await loadCheckinRooms(pin));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 429) {
        setUnlocked(false);
        setPin("");
        setError(err?.response?.data?.error || "That PIN didn't work.");
      }
      // A 403 just means no PIN is configured for rooms yet; the rest of the
      // page still works, so leave the user on it.
    }
  };

  // The item list is public and ~600 entries, so it's fetched once per search
  // rather than held in memory on a kiosk that runs for days.
  const runItemSearch = async (term) => {
    if (term.trim().length < 2) {
      setItemOptions([]);
      return;
    }

    try {
      setLoadingItems(true);
      const resp = await searchItemById(term.trim());
      setItemOptions((resp?.data || []).slice(0, 25));
    } catch {
      setItemOptions([]);
      setError("Couldn't load products. Check the connection and try again.");
    } finally {
      setLoadingItems(false);
    }
  };

  const toStored = (item, previous) => ({
    // Numeric id: the staff portal stores numbers, and a string here would
    // stop matching once Mindbody rebuilds the list.
    id: Number(item.id),
    name: item.name,
    price: item.price,
    type: item.type,
    displayName: previous?.displayName || undefined,
  });

  const closePicker = () => {
    setPicking(null);
    setItemQuery("");
    setItemOptions([]);
  };

  const pickItem = (item) => {
    if (picking?.slot) {
      setPlan((prev) => ({ ...prev, [picking.slot]: toStored(item) }));
    } else if (picking?.list === "featured") {
      setFeatured((prev) => [...prev, toStored(item)]);
    } else if (picking?.list === "others") {
      setPlan((prev) => ({
        ...prev,
        others: [...prev.others, toStored(item)],
      }));
    }
    closePicker();
  };

  const removeItem = (target) => {
    if (target.slot) {
      setPlan((prev) => ({ ...prev, [target.slot]: null }));
    } else if (target.list === "featured") {
      setFeatured((prev) => prev.filter((_, i) => i !== target.index));
    } else {
      setPlan((prev) => ({
        ...prev,
        others: prev.others.filter((_, i) => i !== target.index),
      }));
    }
    setEditing(null);
  };

  const renameItem = (target, displayName) => {
    const applied = (item) => ({
      ...item,
      displayName: displayName.trim() ? displayName : undefined,
    });

    if (target.slot) {
      setPlan((prev) => ({
        ...prev,
        [target.slot]: applied(prev[target.slot]),
      }));
    } else if (target.list === "featured") {
      setFeatured((prev) =>
        prev.map((item, i) => (i === target.index ? applied(item) : item)),
      );
    } else {
      setPlan((prev) => ({
        ...prev,
        others: prev.others.map((item, i) =>
          i === target.index ? applied(item) : item,
        ),
      }));
    }
  };

  const startAddingRoom = async () => {
    setAddingRoom(true);
    setError(null);

    if (rooms) return;

    try {
      setRooms(await loadCheckinRooms(pin));
    } catch (err) {
      setAddingRoom(false);
      setError(
        err?.response?.data?.error ||
          "Couldn't load the rooms for this location.",
      );
    }
  };

  // Reads the chosen file as a data URL — the API takes base64 and puts it on
  // S3, so there's no multipart handling on either side.
  const handleRoomImage = (room, file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setUploadingRoom(room.id);
        setError(null);
        const result = await uploadRoomMap(
          pin,
          room.id,
          room.name,
          reader.result,
        );
        setRoomMaps((result?.roomMaps || []).filter((r) => r?.src));
        setAddingRoom(false);
        toast.success(`Map added for ${room.name}`);
      } catch (err) {
        setError(err?.response?.data?.error || "Couldn't upload that map.");
      } finally {
        setUploadingRoom(null);
      }
    };
    reader.readAsDataURL(file);
  };

  // Portal-created entries are just { id, src }, so fall back to the fetched
  // room list for a name before showing the bare id.
  const roomLabel = (room) =>
    room.name ||
    rooms?.find((r) => String(r.id) === String(room.id))?.name ||
    `Room ${room.id}`;

  const removeRoomMap = (id) =>
    setRoomMaps((prev) =>
      prev.filter((room) => String(room.id) !== String(id)),
    );

  const buildChanges = (withPin) => ({
    ...(withPin && newPin ? { accessPin: newPin } : {}),
    title: form.title,
    subTitle: form.subTitle,
    primaryColor: form.primaryColor,
    secondaryColor: form.secondaryColor,
    timezone: form.timezone,
    logo: form.logo || "",
    ...Object.fromEntries(TOGGLES.map(({ key }) => [key, form[key]])),
    store: {
      // Sent whole: the API replaces the array rather than merging it, so this
      // is also how a removed product is cleared.
      topItems: featured,
    },
    roomMaps,
    planOffer: {
      special: plan.special,
      others: plan.others,
      small: plan.small,
    },
  });

  const persist = async (withPin) => {
    setStatus("saving");
    setError(null);

    try {
      await saveCheckinSettings(pin, buildChanges(withPin));
      await loadLocationPreferences(selectedSiteId, selectedLocationId);

      if (withPin && newPin) {
        setPin(newPin);
        setNewPin("");
      }

      setStatus("saved");
      return true;
    } catch (err) {
      setStatus(null);
      setError(err?.message || "Couldn't save those settings");
      // A rejected PIN sends them back to the pad rather than leaving a dead
      // Save button on screen.
      if (/PIN/i.test(err?.message || "")) {
        setUnlocked(false);
        setPin("");
      }
      return false;
    }
  };

  // Autosave. The PIN field is deliberately excluded — a half-typed PIN would
  // either fail validation or lock everyone out of the kiosk.
  useEffect(() => {
    if (!unlocked || !form) return;

    if (!seeded.current) {
      seeded.current = true;
      return;
    }

    const timer = setTimeout(() => persist(false), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [form, featured, plan, roomMaps]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoImage = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setUploadingLogo(true);
        setError(null);
        const result = await uploadLogo(pin, reader.result);
        // The route saves it server side, so mirror it locally rather than
        // letting the autosave write the old value back over it.
        setForm((prev) => ({ ...prev, logo: result?.logo || "" }));
        toast.success("Logo updated");
      } catch (err) {
        setError(err?.response?.data?.error || "Couldn't upload that logo.");
      } finally {
        setUploadingLogo(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleResetStoreClient = async () => {
    setResettingClient(true);
    setError(null);

    try {
      const result = await resetStoreClient(pin);
      setStoreClientId(result?.id || null);
      toast.success("New store client created");
    } catch (err) {
      setError(
        err?.response?.data?.error || "Couldn't create a new store client.",
      );
    } finally {
      setResettingClient(false);
    }
  };

  const handleSave = async () => {
    if (newPin && !/^\d{4,8}$/.test(newPin)) {
      setError("A new PIN must be 4 to 8 digits.");
      return;
    }

    setBusy(true);
    if (await persist(true)) toast.success("Settings saved");
    setBusy(false);
  };

  const appendDigit = (digit) => {
    setError(null);
    setPin((prev) => (prev.length >= 8 ? prev : prev + digit));
  };

  const sameTarget = (a, b) =>
    !!a && !!b && a.slot === b.slot && a.list === b.list && a.index === b.index;

  const emptyNote = <p className="text-muted m-0 py-2">Nothing set.</p>;

  // One card per product, with rename and remove tucked behind Edit.
  const renderItem = (item, target) => (
    <div
      className="border rounded-3 p-3 mb-3"
      key={`${target.slot || target.list}-${target.index ?? ""}-${item.id}`}
      data-testid={`settings-item-${target.slot || target.list}-${
        target.index ?? 0
      }`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="pe-3">
          <div className="fw-bold">{item.displayName || item.name}</div>
          <div className="text-muted small">
            ${item.price} · {item.type}
          </div>
        </div>

        <button
          type="button"
          className="btn btn-link text-decoration-none text-body flex-shrink-0"
          onClick={() =>
            setEditing(sameTarget(editing, target) ? null : target)
          }
        >
          {sameTarget(editing, target) ? "Done" : "Edit"}
        </button>
      </div>

      {sameTarget(editing, target) && (
        <div className="pt-3">
          {/* Placeholder carries the Mindbody name, so a renamed product is
              still identifiable while editing it. */}
          <input
            className="form-control form-control-sm"
            placeholder={`Show as "${item.name}"`}
            value={item.displayName || ""}
            onChange={(e) => renameItem(target, e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-danger mt-3"
            onClick={() => removeItem(target)}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );

  const renderPicker = (target, canAdd, addLabel) => {
    const open = sameTarget(picking, target);

    if (!open) {
      return (
        canAdd && (
          <div className="text-center mt-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPicking(target)}
            >
              <i className="bi bi-plus-lg me-2"></i>
              {addLabel}
            </button>
          </div>
        )
      );
    }

    return (
      <div className="pt-2">
        <input
          className="form-control"
          placeholder="Search products by name"
          value={itemQuery}
          onChange={(e) => {
            setItemQuery(e.target.value);
            runItemSearch(e.target.value);
          }}
          autoFocus
        />

        {loadingItems && (
          <p className="text-muted small m-0 pt-2">Searching...</p>
        )}

        {!loadingItems && itemOptions.length > 0 && (
          <div
            className="list-group mt-2"
            style={{ maxHeight: 260, overflowY: "auto" }}
          >
            {itemOptions.map((option) => (
              <button
                type="button"
                className="list-group-item list-group-item-action"
                key={`${option.type}-${option.id}`}
                onClick={() => pickItem(option)}
              >
                {option.name}
                <span className="text-muted small">
                  {" "}
                  · ${option.price} · {option.type}
                </span>
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary mt-3"
          onClick={closePicker}
        >
          Cancel
        </button>
      </div>
    );
  };

  return (
    <>
      <GoBack to={`/${selectedSiteId}/${selectedLocationId}`} />

      <div className="container py-5" style={{ maxWidth: 720 }}>
        <div className="text-center pt-5 pb-4">
          <h2 className="fw-light m-0">
            <b>Check-in settings</b>
          </h2>
          <p className="text-muted m-0 pt-2">
            {locationPreferences?.locationName ||
              `Location ${selectedLocationId}`}
          </p>
        </div>

        {error && (
          <div className="alert alert-danger" data-testid="settings-error">
            {error}
          </div>
        )}

        {!unlocked ? (
          <form
            className="bg-white shadow-sm rounded-4 border p-4 text-center"
            onSubmit={handleUnlock}
            autoComplete="off"
          >
            <p className="text-muted">Enter the staff PIN to make changes.</p>

            <input
              type="password"
              inputMode="numeric"
              className="form-control form-control-lg text-center mx-auto"
              style={{ maxWidth: 240, letterSpacing: "0.5em" }}
              data-testid="settings-pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              autoFocus
            />

            <div
              className="d-flex flex-wrap justify-content-center gap-2 pt-4"
              style={{ maxWidth: 240, margin: "0 auto" }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                <button
                  key={digit}
                  type="button"
                  className="btn btn-outline-secondary rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                  style={{ width: 66, height: 66, fontSize: 22 }}
                  onClick={() => appendDigit(String(digit))}
                >
                  {digit}
                </button>
              ))}
              <button
                type="button"
                className="btn btn-outline-secondary rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                style={{ width: 66, height: 66, fontSize: 20 }}
                aria-label="Delete"
                onClick={() => setPin((prev) => prev.slice(0, -1))}
              >
                <i className="bi bi-backspace"></i>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                style={{ width: 66, height: 66, fontSize: 22 }}
                onClick={() => appendDigit("0")}
              >
                0
              </button>
              <button
                type="submit"
                className="btn btn-primary rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                style={{ width: 66, height: 66, fontSize: 22 }}
                data-testid="settings-unlock-btn"
                aria-label="Continue"
              >
                <i className="bi bi-arrow-right"></i>
              </button>
            </div>
          </form>
        ) : (
          form && (
            <>
              <div className="bg-white shadow-sm rounded-4 border p-4 mb-4">
                <div className="pb-3">
                  <label className="form-label">Logo</label>
                  <div className="d-flex align-items-center gap-3">
                    {form.logo ? (
                      <img
                        src={form.logo}
                        alt="Location logo"
                        className="border rounded-3 bg-white p-2"
                        style={{
                          width: 96,
                          height: 96,
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div className="text-muted small">No logo set</div>
                    )}

                    <label className="btn btn-outline-secondary mb-0">
                      {uploadingLogo ? "Uploading..." : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        className="d-none"
                        data-testid="settings-logo-input"
                        onChange={(e) => handleLogoImage(e.target.files?.[0])}
                      />
                    </label>

                    {form.logo && (
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none text-body"
                        onClick={() => setField("logo", "")}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                <div className="pb-3">
                  <label className="form-label" htmlFor="settings-title">
                    Welcome Title
                  </label>
                  <input
                    id="settings-title"
                    className="form-control"
                    data-testid="settings-title-input"
                    placeholder="Welcome!"
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                  />
                </div>

                <div>
                  <label className="form-label" htmlFor="settings-subtitle">
                    Welcome Subtitle
                  </label>
                  <input
                    id="settings-subtitle"
                    className="form-control"
                    data-testid="settings-subtitle-input"
                    placeholder="Start here"
                    value={form.subTitle}
                    onChange={(e) => setField("subTitle", e.target.value)}
                  />
                </div>

                <div className="row">
                  <div className="col-6">
                    <label className="form-label" htmlFor="settings-primary">
                      Primary color
                    </label>
                    <input
                      id="settings-primary"
                      type="color"
                      className="form-control form-control-color color-dot"
                      data-testid="settings-primary-input"
                      value={form.primaryColor}
                      onChange={(e) => setField("primaryColor", e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="settings-secondary">
                      Secondary color
                    </label>
                    <input
                      id="settings-secondary"
                      type="color"
                      className="form-control form-control-color color-dot"
                      data-testid="settings-secondary-input"
                      value={form.secondaryColor}
                      onChange={(e) =>
                        setField("secondaryColor", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="pt-3">
                  <label className="form-label" htmlFor="settings-timezone">
                    Timezone
                  </label>
                  <select
                    id="settings-timezone"
                    className="form-select"
                    data-testid="settings-timezone-select"
                    value={form.timezone}
                    onChange={(e) => setField("timezone", e.target.value)}
                  >
                    {moment.tz.names().map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-white shadow-sm rounded-4 border p-4 mb-4">
                <h5 className="fw-bold">Featured products</h5>
                <p className="text-muted small">
                  Up to {FEATURED_SLOTS} cards on the Purchase screen. Prices
                  come from Mindbody — renaming only changes the wording shown.
                </p>

                {featured.length === 0 && emptyNote}

                {featured.map((item, index) =>
                  renderItem(item, { list: "featured", index }),
                )}

                {renderPicker(
                  { list: "featured" },
                  featured.length < FEATURED_SLOTS,
                  "Add product",
                )}
              </div>

              <div className="bg-white shadow-sm rounded-4 border p-4 mb-4">
                <h5 className="fw-bold">Class prices</h5>
                <p className="text-muted small">
                  Shown when a client is asked to buy before checking in.
                </p>

                <label className="form-label fw-bold">Promotion</label>
                {plan.special
                  ? renderItem(plan.special, { slot: "special" })
                  : emptyNote}
                {renderPicker(
                  { slot: "special" },
                  !plan.special,
                  "Add promotion",
                )}

                <label className="form-label fw-bold mt-4">
                  Pricing options
                </label>
                {plan.others.length === 0 && emptyNote}
                {plan.others.map((item, index) =>
                  renderItem(item, { list: "others", index }),
                )}
                {renderPicker(
                  { list: "others" },
                  plan.others.length < PLAN_OPTION_SLOTS,
                  "Add pricing option",
                )}

                <label className="form-label fw-bold mt-4">Micro offer</label>
                {plan.small
                  ? renderItem(plan.small, { slot: "small" })
                  : emptyNote}
                {renderPicker(
                  { slot: "small" },
                  !plan.small,
                  "Add micro offer",
                )}
              </div>

              <div className="bg-white shadow-sm rounded-4 border p-4 mb-4">
                <h5 className="fw-bold">Behavior</h5>

                {TOGGLES.map(({ key, label }) => (
                  <div className="form-check form-switch py-2" key={key}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`settings-${key}`}
                      data-testid={`settings-${key}-switch`}
                      checked={form[key]}
                      onChange={(e) => setField(key, e.target.checked)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`settings-${key}`}
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </div>

              <div className="bg-white shadow-sm rounded-4 border p-4 mb-4">
                <h5 className="fw-bold">Room maps</h5>
                <p className="text-muted small">
                  Shown after check-in so clients can find their spot. Only
                  rooms with a map are listed.
                </p>

                {roomMaps.length === 0 && emptyNote}

                {roomMaps.map((room) => (
                  <div
                    className="border rounded-3 p-3 mb-3 d-flex justify-content-between align-items-center"
                    key={room.id}
                    data-testid={`settings-roommap-${room.id}`}
                  >
                    <div className="d-flex align-items-center pe-3">
                      <img
                        src={room.src}
                        alt=""
                        className="rounded-3 me-3"
                        style={{ width: 64, height: 64, objectFit: "cover" }}
                      />
                      <div className="fw-bold">{roomLabel(room)}</div>
                    </div>

                    <button
                      type="button"
                      className="btn btn-link text-decoration-none text-body flex-shrink-0"
                      onClick={() => removeRoomMap(room.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}

                {addingRoom ? (
                  <div className="pt-2">
                    {!rooms && (
                      <p className="text-muted small m-0">Loading rooms...</p>
                    )}

                    {rooms && (
                      <div className="list-group">
                        {rooms
                          .filter(
                            (room) =>
                              !roomMaps.some(
                                (m) => String(m.id) === String(room.id),
                              ),
                          )
                          .map((room) => (
                            <label
                              className="list-group-item d-flex justify-content-between align-items-center"
                              key={room.id}
                            >
                              {room.name}
                              <span className="btn btn-sm btn-outline-secondary mb-0">
                                {uploadingRoom === room.id
                                  ? "Uploading..."
                                  : "Upload map"}
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                className="d-none"
                                onChange={(e) =>
                                  handleRoomImage(room, e.target.files?.[0])
                                }
                              />
                            </label>
                          ))}
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-3"
                      onClick={() => setAddingRoom(false)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="text-center mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      data-testid="settings-add-roommap-btn"
                      onClick={startAddingRoom}
                    >
                      <i className="bi bi-plus-lg me-2"></i>
                      Add a room map
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-white shadow-sm rounded-4 border p-4 mb-4">
                <h5 className="fw-bold">Store purchases</h5>
                <p className="text-muted small">
                  Mindbody ties every purchase to a client, so quick product
                  sales are booked against this generic account. Only make a new
                  one if purchases start failing.
                </p>

                <div className="d-flex align-items-center gap-3">
                  <div>
                    <div className="text-muted small">Client ID</div>
                    <div
                      className="fw-bold"
                      data-testid="settings-store-client"
                    >
                      {storeClientId || "Not set"}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-secondary ms-auto"
                    data-testid="settings-store-client-btn"
                    onClick={handleResetStoreClient}
                    disabled={resettingClient}
                  >
                    {resettingClient ? "Creating..." : "Create a new one"}
                  </button>
                </div>
              </div>

              <div className="alert alert-warning rounded-4 p-4 mb-4">
                <label className="form-label" htmlFor="settings-new-pin">
                  Change the staff PIN
                </label>
                <input
                  id="settings-new-pin"
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  data-testid="settings-new-pin-input"
                  placeholder="Leave blank to keep the current PIN"
                  maxLength={8}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                />
                <div className="form-text">
                  4 to 8 digits. Anyone with it can change these settings.
                </div>
              </div>

              <p className="text-muted small">
                Logo, store items and the default store client are set up in MBO
                Tools. This page leaves them alone.
              </p>

              {/* Sits where the clock normally is, which is hidden here. */}
              <div className="settingsSave d-flex align-items-center gap-3">
                <span
                  className="text-muted small"
                  data-testid="settings-status"
                >
                  {status === "saving" && "Saving..."}
                  {status === "saved" && "All changes saved"}
                </span>

                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="settings-save-btn"
                  onClick={handleSave}
                  disabled={busy}
                >
                  {busy ? "Saving..." : "Save settings"}
                </button>
              </div>
            </>
          )
        )}
      </div>
    </>
  );
}
